import { Grid, GridItem, Paragraph } from '@imported/constellation';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CONFIRM_CHECKBOX } from '../../components/FormFields/fieldNames.js';
import { CaptureSelfiePhotoForm, PageHeading } from '../../components/index.js';
import NeedHelp from '../../components/NeedHelp/index.js';
import { proofOfIdentitydefaultValues, } from '../../components/ProofOfIndentityForm/types.js';
import { useIdvProvider } from '../../context/index.js';
import { useLabelContext } from '../../context/LabelContext.js';
import { createDossier as createDossierGuid, docUploadHandler, getExternalToken, retrieveCaseDetails, retrieveDossier, verifyDocuments, } from '../../helper/apiHelper.js';
import { getErrorMessage } from '../../helper/getErrorMessage.js';
import { getSubmitRequest, getToken } from '../../helper/referenceHelper.js';
import { IdvStatus } from '../../types/identityContextTypes.js';
import { FAILURE_STATUS, NOT_APPLICABLE, NUMBER_OF_FILES_EXCEEDED_FOR_DOC_TYPE, SUBMITTED_TO_VENDOR, } from '../../utils/constants.js';
import { IdvResetCountError, IdvSubmitFailure, IdvTokenFailureCaptureSelfie, IdvVerificationError, } from '../../utils/ErrorClasses/index.js';
import { ROUTE_PATHS } from '../../utils/routePaths.js';
const CaptureSelfie = () => {
    const { idvState, setIdvState } = useIdvProvider();
    const { handleSubmit, control, setError, formState: { errors }, } = useForm({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: proofOfIdentitydefaultValues,
    });
    const { applicationProps, idvUploadLabClient, docUploadLabClient, svcIdvClient, getJwt, idvStateHandler, } = idvState;
    const { getLabelFromContext } = useLabelContext();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isApplicable, setIsApplicable] = useState(true);
    const [disableContinue, setDisableContinue] = useState(false);
    const [pageTitle, setPageTitle] = useState(getLabelFromContext('containers.capturePhoto.pageTitle'));
    const [supportiveText, setSupportiveText] = useState('');
    const errorMessages = getLabelFromContext('selfieErrorMessages');
    const submitDataToOurChannel = useCallback(async (idvStateAfterDocUpload, individualData) => {
        await idvStateHandler({
            status: IdvStatus.SUBMIT,
            body: {
                state: idvState.applicationProps,
                idvSubmitData: getSubmitRequest(idvStateAfterDocUpload.getCaseDetails, idvStateAfterDocUpload.createDossier, idvStateAfterDocUpload.getRetrieveDossier, idvStateAfterDocUpload.verifyData, individualData, idvState.applicationProps.brand.toLowerCase(), getToken(), idvState.applicationProps.applicationId, idvState.applicationProps.individualData.individualId, idvState.docType),
            },
        });
    }, [idvStateHandler, idvState.applicationProps, idvState.docType]);
    const handleCapturePhotoSubmission = useCallback(async () => {
        handleSubmit(async (data) => {
            if (!data.confirmCheckbox) {
                setError(CONFIRM_CHECKBOX, {
                    type: 'custom',
                    message: getLabelFromContext('components.capturePhotoForm.confirmation.error'),
                });
                return;
            }
            const clonedIdvState = structuredClone(applicationProps);
            setIsLoading(true);
            try {
                let createJwt;
                let individualData;
                try {
                    const tokenResponse = await getJwt();
                    individualData = {
                        ...applicationProps.individualData,
                        guid: applicationProps.individualData.dossierId,
                        caseRefNumber: tokenResponse.caseRefNumber,
                        identityVerificationSkipped: false,
                    };
                    if (idvState.identityState.idvSaveData) {
                        submitDataToOurChannel(idvState.identityState.idvSaveData, individualData);
                        return;
                    }
                    const externalToken = await getExternalToken(tokenResponse.jwt, applicationProps.brand, svcIdvClient);
                    createJwt = {
                        jwt: externalToken.jwt,
                    };
                }
                catch (error) {
                    setIsLoading(false);
                    await idvStateHandler({
                        status: IdvStatus.ERROR,
                        body: {
                            state: clonedIdvState,
                            error: new IdvTokenFailureCaptureSelfie(`Error at token exchange ${getErrorMessage(error)}`),
                            errorPath: 'captureSelfie',
                        },
                    });
                    return;
                }
                setIsApplicable(true);
                const createDossier = (await createDossierGuid(individualData, createJwt, idvUploadLabClient)).data;
                clonedIdvState.individualData.dossierId = createDossier.guid;
                const getRetrieveDossier = (await retrieveDossier(createJwt, createDossier, idvUploadLabClient)).data;
                if (getRetrieveDossier.status === NOT_APPLICABLE ||
                    getRetrieveDossier.status === SUBMITTED_TO_VENDOR) {
                    setIsApplicable(false);
                    throw new IdvResetCountError(`Error due to Dossier status ${getRetrieveDossier.status}`);
                }
                let getCaseDetails = (await retrieveCaseDetails(individualData, createJwt, docUploadLabClient)).data;
                let docUpload;
                let selfieDoc;
                try {
                    const resDocUpload = await docUploadHandler(createJwt, getCaseDetails, 'document', idvState.capturePhotoResponse, docUploadLabClient);
                    if (resDocUpload) {
                        docUpload = resDocUpload.data;
                    }
                    const resSelfieUpload = await docUploadHandler(createJwt, getCaseDetails, 'selfie', idvState.captureSelfieResponse, docUploadLabClient);
                    if (resSelfieUpload) {
                        selfieDoc = resSelfieUpload.data;
                    }
                }
                catch (error) {
                    if (error.response?.status !== 200 &&
                        error.response.data.message ===
                            NUMBER_OF_FILES_EXCEEDED_FOR_DOC_TYPE) {
                        navigate(`../${ROUTE_PATHS.PROOF_OF_IDENTITY}`);
                        return;
                    }
                    throw new IdvSubmitFailure(`Error while Submitting IDV ${getErrorMessage(error)}`);
                }
                getCaseDetails = (await retrieveCaseDetails(individualData, createJwt, docUploadLabClient)).data;
                const verifyData = (await verifyDocuments(createJwt, createDossier, idvUploadLabClient)).data;
                if (Number(individualData.retryCount) < 2 &&
                    verifyData.outcome === 'RETAKE') {
                    navigate(`../${ROUTE_PATHS.RETAKE_SCREEN}`);
                    throw new IdvVerificationError('Error due to verification failure');
                }
                else {
                    setIdvState((prevState) => ({
                        ...prevState,
                        identityState: {
                            ...prevState.identityState,
                            idvSaveData: {
                                individualData,
                                createJwt,
                                createDossier,
                                getCaseDetails,
                                getRetrieveDossier,
                                docUpload,
                                selfieDoc,
                                verifyData,
                            },
                            individualData,
                        },
                    }));
                    submitDataToOurChannel({
                        getCaseDetails,
                        createDossier,
                        getRetrieveDossier,
                        verifyData,
                    }, individualData);
                }
            }
            catch (error) {
                setIsLoading(false);
                await idvStateHandler({
                    status: IdvStatus.ERROR,
                    body: {
                        state: clonedIdvState,
                        error,
                        errorPath: 'captureSelfie',
                    },
                });
            }
        })();
    }, [
        handleSubmit,
        setError,
        getLabelFromContext,
        idvState.identityState.idvSaveData,
        idvState.capturePhotoResponse,
        idvState.captureSelfieResponse,
        getJwt,
        applicationProps,
        svcIdvClient,
        setIdvState,
        idvUploadLabClient,
        docUploadLabClient,
        submitDataToOurChannel,
        idvStateHandler,
        navigate,
    ]);
    const handleRetakePhoto = useCallback(() => {
        if (idvState.identityState.documentOption === '') {
            navigate(`../${ROUTE_PATHS.PROOF_OF_IDENTITY}`);
        }
        else {
            navigate(`../${ROUTE_PATHS.SELFIE_PHOTO}`);
        }
    }, [idvState.identityState.documentOption, navigate]);
    useEffect(() => {
        const checkPhoto = idvState.captureSelfieResponse;
        if (checkPhoto?.response?.status === FAILURE_STATUS) {
            setDisableContinue(true);
            if (checkPhoto?.response?.warnings && errorMessages) {
                const defaultError = {
                    key: 'MITEK_ERROR_COMMON',
                    heading: 'This photo is too unclear, please try again',
                    supportiveText: 'Make sure your face is in focus and you hold still.',
                };
                const findError = errorMessages.find((el) => el.key === checkPhoto?.response?.warnings?.key) || defaultError;
                setPageTitle(findError.heading);
                setSupportiveText(findError.supportiveText);
            }
        }
        if (!checkPhoto) {
            navigate(`../${ROUTE_PATHS.PROOF_OF_IDENTITY}`);
        }
    }, [
        navigate,
        setDisableContinue,
        disableContinue,
        errorMessages,
        idvState.captureSelfieResponse,
    ]);
    useEffect(() => {
        if (!isApplicable) {
            handleCapturePhotoSubmission();
        }
    }, [handleCapturePhotoSubmission, isApplicable]);
    return (React.createElement(React.Fragment, null,
        React.createElement(PageHeading, { journeyTitle: idvState.labels.applicationHeading, pageTitle: pageTitle }),
        React.createElement(Grid, null,
            React.createElement(GridItem, { md: 8, sm: 12 },
                React.createElement(Paragraph, null, supportiveText),
                React.createElement(CaptureSelfiePhotoForm, { continueFlag: disableContinue, control: control, errors: errors, isLoading: isLoading, handleCapturePhotoSubmission: handleCapturePhotoSubmission, handlePreviousPage: handleRetakePhoto })),
            React.createElement(GridItem, { md: 4, sm: 12 },
                React.createElement(NeedHelp, null)))));
};
export default CaptureSelfie;
//# sourceMappingURL=index.js.map